/**
 * Adds items to the cart and reports the outcome through DOM events.
 *
 * Dispatches on document: `cart:added` (detail: the line item) and
 * `cart:error` (detail: { message }). Also dispatches the theme's existing
 * `cart:refresh` on documentElement so Prestige re-renders the drawer.
 */
class CartApi {
  /**
   * @param {number|string} id Variant id.
   * @param {number} [quantity=1]
   * @param {{ openDrawer?: boolean }} [options]
   * @returns {Promise<object|null>} The line item, or null when the add failed.
   */
  async add(id, quantity = 1, { openDrawer = true } = {}) {
    if (!id) return null;

    const root = window.Shopify?.routes?.root ?? '/';

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        // The `items` array is what makes Shopify validate stock. A flat
        // { id, quantity } body is accepted with no inventory check at all.
        body: JSON.stringify({ items: [{ id, quantity }] }),
      });

      const payload = await response.json();

      if (!response.ok) {
        this.#reportError(payload?.description);
        return null;
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      if (openDrawer) this.#openDrawer();
      document.dispatchEvent(new CustomEvent('cart:added', { detail: payload }));

      return payload;
    } catch {
      this.#reportError();
      return null;
    }
  }

  /** @param {string} [message] Shopify's own description, when it gave one. */
  #reportError(message) {
    document.dispatchEvent(
      new CustomEvent('cart:error', {
        detail: { message: message || window.languages?.cartAddError },
      })
    );
  }

  // Carried over unchanged from the handlers this replaces: the theme opens the
  // drawer directly instead of going through Prestige's Drawer.open().
  #openDrawer() {
    document.querySelector('.PageOverlay')?.classList.add('is-visible');
    document.getElementById('sidebar-cart')?.setAttribute('aria-hidden', 'false');
  }
}

window.cartApi = new CartApi();
